import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { AuthService } from '../../core/auth/auth.service';

@Component({
  selector: 'app-activate-account',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterLink],
  template: `
    <main class="login-premium-page">
      <section class="login-premium-shell">
        <header class="login-premium-topbar glass-card">
          <div class="brand-area">
            <div class="brand-logo"><img src="/favicon.png" alt="The Gherkin Forge" /></div>
            <div class="brand-copy">
              <div class="brand-title">The Gherkin Forge</div>
              <div class="brand-subtitle">Account activation</div>
            </div>
          </div>

          <div class="hero-actions">
            <a class="secondary-button login-topbar-button" routerLink="/login">Back to sign in</a>
          </div>
        </header>

        <section class="login-premium-grid">
          <section class="login-premium-hero">
            <div class="eyebrow">Invitation accepted here</div>
            <h1 class="hero-title">Activate your account and choose your password.</h1>
            <p class="hero-copy login-hero-copy">
              This page is used when an administrator creates your account and sends you an invitation email.
              Set your password once, then sign in to access your workspace.
            </p>
          </section>

          <section class="login-premium-panel">
            <section class="login-card panel-card login-card-premium">
              <div class="login-card-header">
                <div>
                  <div class="eyebrow">Activate my account</div>
                  <h2 class="subsection-title">Create your password</h2>
                </div>
                <div class="status-pill status-pill-success">Secure setup</div>
              </div>

              <p class="section-copy login-card-copy">
                Enter your new password below. Both fields must match.
              </p>

              <form class="login-form-premium" [formGroup]="form" (ngSubmit)="submit()" novalidate>
                <label class="field-block login-field-block">
                  <span>Password</span>
                  <input type="password" formControlName="password" autocomplete="new-password" placeholder="Enter your new password" />
                </label>

                <label class="field-block login-field-block">
                  <span>Confirm password</span>
                  <input type="password" formControlName="confirmPassword" autocomplete="new-password" placeholder="Confirm your new password" />
                </label>

                <div class="feedback login-feedback" *ngIf="feedback() as message" [class.feedback--error]="feedbackType() === 'error'">
                  {{ message }}
                </div>

                <button class="primary-button primary-button--wide login-submit-button" type="submit" [disabled]="loading() || success()">
                  {{ loading() ? 'Activating…' : success() ? 'Account activated' : 'Activate my account' }}
                </button>
              </form>

              <div class="login-divider"></div>

              <div class="login-help-grid">
                <article class="login-help-card">
                  <div class="hero-proof-label">Next step</div>
                  <p>Once activation is complete, use your email or username and your new password to sign in.</p>
                </article>

                <article class="login-help-card">
                  <div class="hero-proof-label">Need to sign in now?</div>
                  <a class="secondary-button" routerLink="/login">Go to sign in</a>
                </article>
              </div>
            </section>
          </section>
        </section>
      </section>
    </main>
  `
})
export class ActivateAccountComponent {
  private readonly fb = inject(FormBuilder);
  private readonly route = inject(ActivatedRoute);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);

  readonly loading = signal(false);
  readonly success = signal(false);
  readonly feedback = signal<string | null>(null);
  readonly feedbackType = signal<'info' | 'error'>('info');
  readonly token = computed(() => (this.route.snapshot.queryParamMap.get('token') || '').trim());

  readonly form = this.fb.nonNullable.group({
    password: ['', [Validators.required, Validators.minLength(8)]],
    confirmPassword: ['', [Validators.required, Validators.minLength(8)]]
  });

  submit(): void {
    if (!this.token()) {
      this.feedbackType.set('error');
      this.feedback.set('Invitation token is missing or invalid.');
      return;
    }

    if (this.form.invalid || this.loading()) {
      this.form.markAllAsTouched();
      this.feedbackType.set('error');
      this.feedback.set('Please enter a password of at least 8 characters in both fields.');
      return;
    }

    const { password, confirmPassword } = this.form.getRawValue();
    if (password !== confirmPassword) {
      this.feedbackType.set('error');
      this.feedback.set('Passwords do not match.');
      return;
    }

    this.loading.set(true);
    this.feedbackType.set('info');
    this.feedback.set('Activating your account...');

    this.authService.acceptInvitation(this.token(), password).subscribe({
      next: () => {
        this.success.set(true);
        this.feedbackType.set('info');
        this.feedback.set('Your account has been activated. Redirecting to sign in...');
        window.setTimeout(() => {
          void this.router.navigate(['/login']);
        }, 1200);
      },
      error: (error) => {
        this.loading.set(false);
        this.feedbackType.set('error');
        const code = error?.error?.error;
        this.feedback.set(
          code === 'INVITATION_TOKEN_INVALID'
            ? 'This activation link is invalid or has already been used.'
            : code === 'PASSWORD_TOO_SHORT'
              ? 'Your password must contain at least 8 characters.'
              : 'Unable to activate your account.'
        );
      },
      complete: () => this.loading.set(false)
    });
  }
}
